import axios from 'axios';
import { toast } from 'react-hot-toast';

type ApiParams = Record<string, string | number | boolean | undefined>;
type ApiPayload = Record<string, unknown>;

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:3000/api' : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const requestUrl = String(error.config?.url || '');
      const isLoginRequest = requestUrl.includes('/auth/login');
      const hasToken = Boolean(localStorage.getItem('auth_token'));
      
      switch (status) {
        case 401:
          if (isLoginRequest || !hasToken) {
            toast.error(data.message || 'Credenciales inválidas.');
            break;
          }

          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
          break;
        case 403:
          toast.error('No tienes permisos para realizar esta acción.');
          break;
        case 404:
          toast.error('Recurso no encontrado.');
          break;
        case 409:
          toast.error(data.message || 'Conflicto: el recurso ya existe.');
          break;
        case 422:
          toast.error('Datos inválidos. Por favor, revisa el formulario.');
          break;
        case 500:
          toast.error('Error interno del servidor. Por favor, intenta más tarde.');
          break;
        default:
          toast.error(data.message || 'Ha ocurrido un error inesperado.');
      }
    } else if (error.request) {
      toast.error('No se pudo conectar con el servidor. Verifica tu conexión.');
    } else {
      toast.error('Error al procesar la solicitud.');
    }
    
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  googleLogin: (idToken: string) =>
    api.post('/auth/google', { idToken }),
  register: (data: ApiPayload) =>
    api.post('/auth/register', data),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
  getProfile: () =>
    api.get('/auth/profile'),
  updateProfile: (data: { name?: string; email?: string; avatarUrl?: string | null }) =>
    api.patch('/auth/profile', data),
};

export const clientsApi = {
  getAll: (params?: ApiParams) =>
    api.get('/clients', { params }),
  search: (query: string) =>
    api.get('/clients/search', { params: { q: query } }),
  getById: (id: string) =>
    api.get(`/clients/${id}`),
  create: (data: ApiPayload) =>
    api.post('/clients', data),
  update: (id: string, data: ApiPayload) =>
    api.patch(`/clients/${id}`, data),
  delete: (id: string) =>
    api.delete(`/clients/${id}`),
  getTop: (limit: number = 5) =>
    api.get('/clients/top', { params: { limit } }),
  getStats: (id: string) =>
    api.get(`/clients/${id}/stats`),
};

export const salesApi = {
  getAll: (params?: ApiParams) =>
    api.get('/sales', { params }),
  getRecent: (limit: number = 10) =>
    api.get('/sales/recent', { params: { limit } }),
  getToday: () =>
    api.get('/sales/today'),
  getStats: (period: 'day' | 'week' | 'month' | 'year') =>
    api.get('/sales/stats', { params: { period } }),
  getMonthly: (year: number) =>
    api.get(`/sales/monthly/${year}`),
  getById: (id: string) =>
    api.get(`/sales/${id}`),
  create: (data: ApiPayload) =>
    api.post('/sales', data),
  update: (id: string, data: ApiPayload) =>
    api.patch(`/sales/${id}`, data),
  delete: (id: string) =>
    api.delete(`/sales/${id}`),
};

export const dashboardApi = {
  getDashboard: () =>
    api.get('/dashboard'),
  getMonthlyOverview: (year: number) =>
    api.get(`/dashboard/monthly/${year}`),
  getClientPerformance: (clientId: string) =>
    api.get(`/dashboard/client/${clientId}`),
  getQuickStats: () =>
    api.get('/dashboard/quick-stats'),
};

export const userTargetsApi = {
  getCurrent: (month?: number, year?: number) =>
    api.get('/user-targets/current', { params: { month, year } }),
  upsertCurrent: (data: { target: number; month: number; year: number }) =>
    api.put('/user-targets/current', data),
};

export const healthApi = {
  check: () => api.get('/health'),
};

export default api;
